import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';

import { BehaviorSubject, Observable, Subscription, timer } from 'rxjs';
import { finalize } from 'rxjs/operators';

import { environment } from 'src/environments/environment';
import { StoreNames } from 'src/app/constants/storage-names';

import { SessionService } from './storage/session.service';

import { ApiStatus } from 'src/app/models/api';
import { SessionStatus } from '../constants/session';
import { ApiHeaderType } from '../constants/api-header-type';
import { StyleService } from './styles.service';
import { MatDialog } from '@angular/material/dialog';
import { DialogTimeoutComponent } from '../components/shared/dialog/timeout.component';
import { Title } from '@angular/platform-browser';

@Injectable()
export class ApiService {
	public requireTokenRefresh: boolean;
	public isSessionTokenRefreshPath: boolean;
	public $RefreshTokenSubject = new BehaviorSubject<string | null>(null);

	public apiStatus = new ApiStatus();
	public $ApiStatus = new BehaviorSubject(this.apiStatus);
	public storageStore: Observable<any>;

	private lastAPICall: Date;
	private logoutTimerDate: Date;
	private logoutAgentMinutes: number = 35;
	private logoutMemberMinutes: number = 15;
	private timerSubscription: Subscription | null = null;
	private originalTitle = '';
	private blinkInterval: any;

	constructor(
		private http: HttpClient,
		private sessionService: SessionService,
		private styleService: StyleService,
		public dialog: MatDialog,
		private title: Title
	) {
		this.originalTitle = this.title.getTitle();
		this.storageStore = this.sessionService.$SessionClear;
		this.storageStore.subscribe((status: string) => {
			switch (status) {
				case SessionStatus.initalize:
					let sessionState = this.sessionService.retrieve(StoreNames.apiStatus);
					if (sessionState) {
						this.apiStatus = sessionState;
						this.apiStatus.numberOfCalls = 0;
						this.apiStatus.isActive = false;
						this.$ApiStatus.next(this.apiStatus);
					}
					break;

				case SessionStatus.clearSession:
					this.$ApiStatus.next(this.apiStatus = new ApiStatus());
					break;
			}
		});
	}

	public serializeString(string: string): string {
		return encodeURIComponent(string);
	}

	private setStore(): void {
		this.sessionService.store(StoreNames.apiStatus, this.apiStatus);
		this.$ApiStatus.next(this.apiStatus);
	}

	public get(route: string, apiHeaderType: ApiHeaderType = ApiHeaderType.json): Observable<any> {
		this.setApiStatus();
		return this.http.get(this.buildRoute(route), this.buildHeaders(apiHeaderType)).pipe(
			finalize(() => this.setApiStatus(true)),
		);
	}

	public post<T>(route: string, payload: T, apiHeaderType: ApiHeaderType = ApiHeaderType.json): Observable<any> {
		this.setApiStatus();
		return this.http.post(this.buildRoute(route), payload, this.buildHeaders(apiHeaderType)).pipe(
			finalize(() => this.setApiStatus(true)),
		);
	}

	public postNoSpinner<T>(route: string, payload: T, apiHeaderType: ApiHeaderType = ApiHeaderType.json): Observable<any> {
		return this.http.post(this.buildRoute(route), payload, this.buildHeaders(apiHeaderType)).pipe();
	}

	public put<T>(route: string, payload: T, apiHeaderType: ApiHeaderType = ApiHeaderType.json): Observable<any> {
		this.setApiStatus();
		return this.http.put(this.buildRoute(route), payload, this.buildHeaders(apiHeaderType)).pipe(
			finalize(() => this.setApiStatus(true)),
		);
	}

	public delete(route: string, apiHeaderType: ApiHeaderType = ApiHeaderType.json): Observable<any> {
		this.setApiStatus();
		return this.http.delete(this.buildRoute(route), this.buildHeaders(apiHeaderType)).pipe(
			finalize(() => this.setApiStatus(true)),
		);
	}

	public setAuthToken(p_Token: string, p_RefreshToken: string): void {
		this.apiStatus = new ApiStatus();
		this.apiStatus.authToken = p_Token;
		this.apiStatus.refreshToken = p_RefreshToken;
		this.setStore();
	}

	public refreshTokens(p_Token: string, p_RefreshToken: string): void {
		this.apiStatus.authToken = p_Token;
		this.apiStatus.refreshToken = p_RefreshToken;
		this.setStore();
	}

	private buildRoute(route: string): string {
		if (!route.startsWith('/')) {
			throw 'APIService missing "/" on route or controllerRoute, stackTrace APIService.buildRoute';
		}
		return `${environment.api}${route}`;
	}

	private buildHeaders(apiHeaderType: ApiHeaderType) {
		let headers = new HttpHeaders();

		if (this.apiStatus.authToken) headers = headers.set('Authorization', `Bearer ${this.apiStatus.authToken}`);
		if (this.sessionService.session.language) headers = headers.set('Content-Language', `${this.sessionService.session.language}`);

		switch (apiHeaderType) {
			case ApiHeaderType.json:
				headers = headers.set('Accept', 'application/json, text/plain, */*');
				headers = headers.set('Content-Type', 'application/json');
				break;

			case ApiHeaderType.blob:
				headers = headers.set('Accept', 'application/json, text/plain, */*');
				headers = headers.set('Content-Type', 'application/json');

				return {
					headers,
					withCredentials: true,
					responseType: 'blob' as 'json'
				};

			case ApiHeaderType.formData:
				headers = headers.set('Accept', 'application/json, text/plain, */*');
				/**
				 * Form Data Content Type requrest the following: 
				 * multipart/form-data; boundary=----WebKitFormBoundaryxxxKeyxxx
				 * WebKitFormBoundary is auto generated by the browser.
				 * Not setting the content type manualy auto generates the conenten type header
				 * 
				 * headers = headers.set('Content-Type', 'multipart/form-data');
				 */
				break;
		}

		return {
			headers,
			withCredentials: true
		};
	}

	/**
	 * App-auto-disable directive to disable page controls when api is running.
	 * Directive adds selector on top div on app.component.html and disables all children elements.
	 */
	public setApiStatus(isCallComplete: boolean = false): void {
		if (isCallComplete) {
			this.apiStatus.numberOfCalls--;
			this.loginTimerCheck();
		} else {
			this.apiStatus.numberOfCalls++;
		}

		if (this.apiStatus.numberOfCalls < 0)
			this.apiStatus.numberOfCalls = 0;

		this.apiStatus.isActive = this.apiStatus.numberOfCalls !== 0;

		if (this.apiStatus.isActive)
			this.styleService.setDocumentStyle('cursor', 'wait');
		else
			this.styleService.removeStyle('cursor');

		this.$ApiStatus.next(this.apiStatus);
	}

	private loginTimerCheck(): void {
		this.lastAPICall = new Date();
		if (this.sessionService.session.isLoggedIn && !this.timerSubscription) {
			this.startTimer(new Date());
		}
	}

	public startTimer(timerDate: Date, timerMinuteOffset: number = 0) {
		this.logoutTimerDate = timerDate;

		let l_LogoutMinutes: number = this.logoutMemberMinutes;
		if (this.sessionService.session.isAgent || this.sessionService.session.isAdmin)
			l_LogoutMinutes = this.logoutAgentMinutes;

		const timerMs = (l_LogoutMinutes - timerMinuteOffset) * 60 * 1000;
		this.timerSubscription = timer(timerMs).subscribe(() => {

			// Although we're doing 50 minutes, let's run the check in 38 minutes.
			this.logoutTimerDate.setMinutes(this.logoutTimerDate.getMinutes() + 12);

			this.timerSubscription.unsubscribe();
			if (!this.lastAPICall || this.logoutTimerDate > this.lastAPICall) {
				this.openTimeoutDialog();
			} else {
				this.startTimer(this.lastAPICall, Math.floor(new Date().getTime() - this.lastAPICall.getTime() / 60000));
			}
		});
	}

	public cancelTimer() {
		if (this.timerSubscription) {
			this.timerSubscription.unsubscribe();
			this.timerSubscription = null;
		}
	}

	startBlinkingTitle() {
		let showAlert = false;
		this.blinkInterval = setInterval(() => {
			this.title.setTitle(
				showAlert ? '⚠️ Session Expiring!' : this.originalTitle
			);
			showAlert = !showAlert;
		}, 1700);
	}

	private openTimeoutDialog(): void {
		this.startBlinkingTitle();
		const dialogRef = this.dialog.open(DialogTimeoutComponent, {
			panelClass: 'mwp-modal-override-500',
			disableClose: true
		});

		dialogRef.afterClosed().subscribe(result => {
			if (this.sessionService?.session?.isLoggedIn) {
				this.startTimer(new Date());
			} else {
				this.cancelTimer();
			}
			clearInterval(this.blinkInterval);
			this.title.setTitle(this.originalTitle);
		});
	}
}